import { useEffect, useRef, useState } from 'react';

import {
  ExplainerVideoContext,
  trackExplainerVideoClosed,
  trackExplainerVideoCollapsed,
  trackExplainerVideoCompleted,
  trackExplainerVideoExpanded,
  trackExplainerVideoPlayed,
  trackExplainerVideoProgress,
  trackExplainerVideoShown,
} from '../services/analytics/marketplaceEvents';

const COLLAPSED_KEY = 'marketplace-explainer-video-collapsed';
const VIDEO_SRC = '/leaseend-com-images/marketplace/explainer-v1.mp4';
const MILESTONES = [25, 50, 75] as const;
type View = 'card' | 'player' | 'collapsed';

const initialView = (): View => {
  try {
    return sessionStorage.getItem(COLLAPSED_KEY) === 'true' ? 'collapsed' : 'card';
  } catch {
    return 'card';
  }
};

const stopVideo = (video: HTMLVideoElement | null) => {
  if (!video?.getAttribute('src')) return;
  video.pause();
  video.removeAttribute('src');
  video.load();
};

const useMarketplaceExplainerVideo = (context: ExplainerVideoContext) => {
  const [view, setView] = useState<View>(initialView);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardShown = useRef(false);
  const hasPlayed = useRef(false);
  const milestones = useRef(new Set<number>());
  const restoreFocus = useRef(false);

  useEffect(() => {
    if (view === 'card' && !cardShown.current) trackExplainerVideoShown(context);
    cardShown.current = view === 'card';
    if (restoreFocus.current) {
      const target =
        view === 'player' ? videoRef.current : containerRef.current?.querySelector('button');
      target?.focus();
      restoreFocus.current = false;
    }
  }, [view, context]);

  useEffect(() => {
    const video = videoRef.current;
    return () => stopVideo(video);
  }, []);

  const setCollapsed = (collapsed: boolean) => {
    try {
      sessionStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
    restoreFocus.current = true;
    setView(collapsed ? 'collapsed' : 'card');
    if (collapsed) trackExplainerVideoCollapsed(context);
    else {
      trackExplainerVideoExpanded(context);
    }
  };

  const play = () => {
    const video = videoRef.current;
    if (!video) return;
    hasPlayed.current = false;
    milestones.current.clear();
    setHasError(false);
    restoreFocus.current = true;
    setView('player');
    video.src = VIDEO_SRC;
    video.muted = false;
    video.play().catch(() => {
      // Native controls remain available if the browser rejects playback.
    });
  };

  const returnToCard = () => {
    restoreFocus.current = !!containerRef.current?.contains(document.activeElement);
    stopVideo(videoRef.current);
    setView('card');
  };

  const onPlaying = () => {
    if (view !== 'player' || hasPlayed.current) return;
    hasPlayed.current = true;
    trackExplainerVideoPlayed(context);
  };

  const onTimeUpdate = () => {
    const video = videoRef.current;
    if (view !== 'player' || !hasPlayed.current || !video || !Number.isFinite(video.duration))
      return;
    if (video.duration <= 0 || video.seeking) return;
    MILESTONES.forEach((percent) => {
      if (video.currentTime / video.duration < percent / 100 || milestones.current.has(percent))
        return;
      milestones.current.add(percent);
      trackExplainerVideoProgress(context, percent);
    });
  };

  const onEnded = () => {
    if (view !== 'player' || !hasPlayed.current) return;
    onTimeUpdate();
    trackExplainerVideoCompleted(context);
    returnToCard();
  };

  const close = () => {
    trackExplainerVideoClosed(context, videoRef.current?.currentTime ?? 0);
    returnToCard();
  };

  return {
    view,
    hasError,
    videoRef,
    containerRef,
    play,
    close,
    setCollapsed,
    onPlaying,
    onTimeUpdate,
    onEnded,
    onError: () => setHasError(true),
  };
};

export default useMarketplaceExplainerVideo;
